import { useState } from 'react';

import { fetchPutSellerPayLink } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';

import styles from './ActivePayLinks.module.css';
import { LinkWithQuery } from '../../../components/LinkWithQuery/LinkWithQuery';
import { SellerNavBar } from '../../../components/NavBars/SellerNavBar/SellerNavBar';
import { PageTemplate } from '../../../components/PageTemplate/PageTemplate';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import filterIcon from '../assets/filterIcon.svg';
import whiteCross from '../assets/whiteCross.svg';
import { FilterDrawer } from '../components/FilterDrawer/FilterDrawer';
import { Pagination } from '../components/Pagination/Pagination';
import { PayLinkTable } from '../components/PayLinkTable/PayLinkTable';
import { PaymentsSearch } from '../components/PayLinkTable/PaymentsSearch/PaymentsSearch';
import { usePayLinks } from '../hooks/usePaylinks.hook';

export const ActivePayLinks = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [checkedPayLinksIds, setCheckedPayLinksIds] = useState<string[]>([]);

  const { payLinks, fetchPayLinks, paginationLinks } = usePayLinks({
    isArchived: false,
    page,
    searchQuery,
  });
  const {
    totalCount: archivedPayLinksCount,
    fetchPayLinks: fetchArchivedPayLinks,
  } = usePayLinks({
    isArchived: true,
    searchQuery,
  });

  const { stateKey } = useStateKey();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilterSearchParam = searchParams.get('status');
  const resetStatusParam = () => {
    setSearchParams(undefined);
  };

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');

  const archivePayLinks = async (payLinkIds: string[]) => {
    const response = await fetchPutSellerPayLink({
      stateKey: stateKey!,
      body: {
        pay_link_ids: payLinkIds,
        is_archived: true,
      },
    });
    if (response.success) {
      setCheckedPayLinksIds([]);
    }
    await fetchPayLinks();
    await fetchArchivedPayLinks();
  };

  return (
    <PageTemplate NavBar={() => <SellerNavBar />}>
      <div className={styles.page}>
        {isFilterPanelOpen && (
          <div className={styles.filterDrawerBackdrop}></div>
        )}
        <FilterDrawer
          isOpen={isFilterPanelOpen}
          setIsOpen={setIsFilterPanelOpen}
          selectedStatusFilter={selectedStatusFilter}
          setSelectedStatusFilter={setSelectedStatusFilter}
        />
        <div className={styles.content}>
          <div className={styles.pageHeader}>Payments</div>
          <div className={styles.paymentsActions}>
            <div className={styles.leftHandSide}>
              <div className={styles.archiveItems}>
                <Button
                  variant="tertiary"
                  className={classNames(
                    checkedPayLinksIds.length
                      ? styles.activeAction
                      : styles.inactiveAction,
                  )}
                  onClick={() => archivePayLinks(checkedPayLinksIds)}
                  disabled={!checkedPayLinksIds.length}
                >
                  Archive
                </Button>
                <LinkWithQuery
                  to="/pay/payments/archived/"
                  className={styles.actionBarLink}
                >
                  Show Archive ({archivedPayLinksCount ?? 0}) {'>'}
                </LinkWithQuery>
              </div>
              <div className={styles.filterItems}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsFilterPanelOpen(true);
                    if (!statusFilterSearchParam) {
                      setSelectedStatusFilter('');
                    } else {
                      setSelectedStatusFilter(statusFilterSearchParam);
                    }
                  }}
                >
                  Filter
                  <img
                    className={styles.iconSVG}
                    src={filterIcon}
                    alt="Open Filters"
                  />
                </Button>
                {statusFilterSearchParam && (
                  <Button variant="denim" onClick={resetStatusParam}>
                    1 filter active
                    <img
                      className={styles.iconSVG}
                      src={whiteCross}
                      alt="Remove Filter"
                    />
                  </Button>
                )}
              </div>
            </div>
            <div className={styles.rightHandSide}>
              <PaymentsSearch
                onChange={(query: string) => {
                  setSearchQuery(query);
                }}
              />
            </div>
          </div>
          <PayLinkTable
            payLinks={payLinks}
            checkedPayLinksIds={checkedPayLinksIds}
            setCheckedPayLinksIds={setCheckedPayLinksIds}
          />
        </div>
        <Pagination
          currentPageNumber={page}
          onPageSelect={(page?: number) => {
            if (!page) return;

            setPage(page);
          }}
          paginationLinks={paginationLinks}
        />
      </div>
    </PageTemplate>
  );
};
